import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LiveKitRoom, RoomAudioRenderer, GridLayout, ParticipantTile,
  useTracks, useLocalParticipant, useRemoteParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { Video, ShieldAlert, ShieldCheck, Loader2, Stethoscope } from "lucide-react";

/**
 * LiveKitCall — replaces JitsiCall.jsx.
 *
 * Contract (same as JitsiCall so migration is a rename at the call sites):
 *   roomName, displayName, email?, jwt (LiveKit token), domain (LiveKit
 *   wss:// URL — kept named "domain" for drop-in compatibility), onLeave,
 *   autoJoin, nativeToolbar (unused — LiveKit chrome is hidden), onApiReady.
 *
 * Behaviour:
 *  * `onApiReady({ toggleAudio, toggleVideo, hangup })` — exposes the same
 *    imperative surface the ConsultationStage already drives, so the
 *    custom control bar keeps working with a one-line rename
 *    (`jitsiCommand(api, 'toggleAudio')` → `apiRef.current.toggleAudio()`).
 *  * If `jwt` is null the component renders a "not configured" banner
 *    instead of connecting — same demo fallback as before.
 *  * HIPAA badge lit only when a signed token is present.
 *  * LiveKit's Room object handles its own DOM cleanup — no manual
 *    `removeChild` gymnastics needed (this was the source of the removeChild
 *    unmount error we hit in iteration 12 with Jitsi).
 */

function ControlAdapter({ onApiReady, onLeave }) {
  // useLocalParticipant only works inside LiveKitRoom; this component sits
  // one level down and adapts the LiveKit control surface into the same
  // imperative API our custom control bar already speaks to.
  const { localParticipant } = useLocalParticipant();

  const toggleAudio = useCallback(async () => {
    if (!localParticipant) return;
    const on = localParticipant.isMicrophoneEnabled;
    await localParticipant.setMicrophoneEnabled(!on);
  }, [localParticipant]);

  const toggleVideo = useCallback(async () => {
    if (!localParticipant) return;
    const on = localParticipant.isCameraEnabled;
    await localParticipant.setCameraEnabled(!on);
  }, [localParticipant]);

  const hangup = useCallback(() => {
    // The parent LiveKitRoom listens to `onDisconnected` — we just detach
    // the local tracks and let LiveKit fire the disconnect event.
    onLeave?.();
  }, [onLeave]);

  useEffect(() => {
    // Expose an object shaped to match the old `jitsiCommand(api, cmd)`
    // callers upstream (they call `.toggleAudio()`, `.toggleVideo()`,
    // `.hangup()`).
    onApiReady?.({ toggleAudio, toggleVideo, hangup });
  }, [toggleAudio, toggleVideo, hangup, onApiReady]);

  return null;
}

/**
 * Camera layout. Screen share is intentionally NOT surfaced.
 *
 * Interpreter mode: when a visitor whose identity carries the
 * `interpreter` role is in the room, the guest and doctor stay large and
 * face to face, and the interpreter sits in a small corner tile — always
 * visible, never the focus. Identity shape: `invite:<role>:<invite_id>`.
 */
function isInterpreter(track) {
  const id = track?.participant?.identity || "";
  return id.startsWith("invite:interpreter:") || id.startsWith("invite:translator:");
}

function VideoGrid() {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false },
  );
  const interpreters = tracks.filter(isInterpreter);
  const principals = tracks.filter((t) => !isInterpreter(t));

  if (interpreters.length === 0 || principals.length === 0) {
    return (
      <GridLayout tracks={tracks} className="h-full w-full">
        <ParticipantTile />
      </GridLayout>
    );
  }

  return (
    <div className="relative h-full w-full" data-testid="interpreter-layout">
      <GridLayout tracks={principals} className="h-full w-full">
        <ParticipantTile />
      </GridLayout>
      <div className="absolute bottom-24 right-3 z-40 w-28 sm:w-36 rounded-xl overflow-hidden border border-cream/25 shadow-lg"
           data-testid="interpreter-tile">
        <GridLayout tracks={interpreters} className="h-full w-full">
          <ParticipantTile />
        </GridLayout>
        <div className="absolute bottom-0 inset-x-0 bg-black/55 text-cream text-[9px] uppercase tracking-widest text-center py-0.5">
          Interpreter
        </div>
      </div>
    </div>
  );
}

/**
 * WaitingRoom — full-cover overlay shown to a non-doctor participant
 * while the doctor has not yet joined the LiveKit room. Vanishes the
 * moment any RemoteParticipant with an identity starting `doctor:`
 * appears.
 *
 * Identity contract (see backend/routers/livekit.py):
 *   authenticated users → `<role>:<user_id>`  (e.g. `doctor:7e6...`)
 *   anonymous invitees  → `invite:<invite_id>`
 * So checking `startsWith("doctor:")` on the remote participant list is
 * both necessary and sufficient. The local identity is used only to
 * silence the overlay when the doctor themselves is watching.
 */
function WaitingRoom() {
  const { localParticipant } = useLocalParticipant();
  const remotes = useRemoteParticipants();

  const isAttendee = !(localParticipant?.identity || "").startsWith("doctor:");
  const doctorHere = remotes.some((p) => (p.identity || "").startsWith("doctor:"));

  if (!isAttendee || doctorHere) return null;

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(15,31,20,0.86)] text-cream"
      data-testid="livekit-waiting-room"
    >
      {/* Local heartbeat CSS — keeps the animation self-contained so this
          file doesn't leak keyframes into the global stylesheet. */}
      <style>{`
        @keyframes lk-heartbeat {
          0%, 100% { transform: scale(1); }
          15% { transform: scale(1.18); }
          30% { transform: scale(1); }
          45% { transform: scale(1.10); }
          60% { transform: scale(1); }
        }
        @keyframes lk-ripple {
          0% { transform: scale(0.85); opacity: 0.55; }
          100% { transform: scale(1.65); opacity: 0; }
        }
      `}</style>
      <div className="text-center px-8 max-w-sm" role="status" aria-live="polite">
        <div className="relative mx-auto h-24 w-24 mb-6">
          {/* Two staggered ripples for the "listening for connection" feel */}
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2 border-sage-500/60"
            style={{ animation: "lk-ripple 2.4s ease-out infinite" }}
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2 border-sage-500/60"
            style={{ animation: "lk-ripple 2.4s ease-out 1.2s infinite" }}
          />
          <div
            className="absolute inset-0 rounded-full bg-sage-500/25 border border-sage-500/60 flex items-center justify-center"
            style={{ animation: "lk-heartbeat 1.6s ease-in-out infinite" }}
            data-testid="livekit-waiting-heartbeat"
          >
            <Stethoscope className="h-9 w-9 text-cream" strokeWidth={1.5} />
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.28em] text-cream/60">
          Secure room
        </div>
        <h2 className="font-serif text-3xl mt-2 leading-tight">
          Your doctor is joining
        </h2>
        <p className="text-sm mt-4 text-cream/75 leading-relaxed">
          Settle in — the physician has been notified and will appear in a moment.
          Your camera and microphone are already connected.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-cream/60">
          <span className="h-1.5 w-1.5 rounded-full bg-sage-500 pulse-dot" />
          Waiting for physician
        </div>
      </div>
    </div>
  );
}

export function LiveKitCall({
  jwt,
  domain,           // wss:// URL — kept name for drop-in with old callers
  onLeave,
  autoJoin = true,
  onApiReady,
  blocked,          // {code, message} — why the server withheld a token
  onResolveBlock,   // async () => void — clears the blocker and refetches
}) {
  const [status, setStatus] = useState(autoJoin && jwt && domain ? "joining" : "idle");
  const [error, setError] = useState(null);
  const attemptedRef = useRef(false);

  const hipaaMode = !!jwt && !!domain;
  const canConnect = hipaaMode;

  // Trigger a re-render + auto-join when the token/URL arrive after mount.
  useEffect(() => {
    if (autoJoin && canConnect && !attemptedRef.current) {
      attemptedRef.current = true;
      setStatus("joining");
    }
  }, [autoJoin, canConnect]);

  const banner = useMemo(() => {
    if (blocked) return null;   // the centre card already explains the hold
    if (hipaaMode) {      return (
        <div
          className="absolute top-3 left-3 z-10 h-6 w-6 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center pointer-events-auto"
          title="HIPAA · signed LiveKit session"
          aria-label="HIPAA signed session"
          data-testid="livekit-hipaa-active"
        >
          <ShieldCheck className="h-3 w-3 text-emerald-100" />
        </div>
      );
    }
    return (
      <div
        className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-amber-500/30 text-amber-50 border border-amber-300/50 pointer-events-auto"
        data-testid="livekit-not-configured-warning"
      >
        <ShieldAlert className="h-3 w-3" />
        Video unavailable
      </div>
    );
  }, [hipaaMode, blocked]);

  return (
    <div
      className="rounded-2xl border border-forest-deep bg-forest-deep overflow-hidden relative w-full h-full"
      data-testid="livekit-container"
    >
      {canConnect ? (
        <LiveKitRoom
          serverUrl={domain}
          token={jwt}
          connect={autoJoin || status === "joining"}
          audio
          video
          onConnected={() => setStatus("in-call")}
          onDisconnected={() => {
            setStatus("idle");
            onLeave?.();
          }}
          onError={(e) => {
            setError(String(e?.message || e));
            setStatus("idle");
          }}
          data-lk-theme="default"
          className="h-full w-full"
        >
          <VideoGrid />
          <RoomAudioRenderer />
          <ControlAdapter onApiReady={onApiReady} onLeave={onLeave} />
          <WaitingRoom />
        </LiveKitRoom>
      ) : (
        <div className="absolute inset-0 bg-[#23342A]" />
      )}

      {/* HUD overlay (banner + idle/joining states). */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-cream">
        {banner}

        {!canConnect && status === "idle" && (
          <div className="text-center px-6 pointer-events-auto" data-testid="livekit-fallback">
            <div className="w-14 h-14 rounded-full bg-cream/10 flex items-center justify-center mx-auto mb-3">
              <Video className="h-6 w-6 text-cream" />
            </div>
            {blocked?.code === "consent_required" ? (
              <>
                <div className="eyebrow" style={{ color: "rgba(234,221,203,0.7)" }}>
                  One thing first
                </div>
                <div className="font-serif text-xl mt-1">Agree to a video consultation</div>
                <p className="text-sm mt-2 max-w-xs mx-auto opacity-80">
                  Before we connect you, please confirm you&apos;re happy to be seen by video.
                  You can withdraw this at any time in your privacy settings.
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    try { await onResolveBlock?.(); }
                    catch { setError("We couldn't save that just now — please try again."); }
                  }}
                  className="mt-4 h-11 px-6 rounded-full bg-cream text-[#23342A] text-sm font-semibold"
                  data-testid="livekit-grant-consent-btn"
                >
                  I agree — connect me
                </button>
              </>
            ) : blocked?.code === "payment_required" ? (
              <>
                <div className="eyebrow" style={{ color: "rgba(234,221,203,0.7)" }}>
                  Payment pending
                </div>
                <div className="font-serif text-xl mt-1">Your consultation isn&apos;t settled yet</div>
                <p className="text-sm mt-2 max-w-xs mx-auto opacity-80">
                  Settle the fee — by card, to your room or through your company — and the
                  room will open straight away.
                </p>
              </>
            ) : (
              <>
                <div className="eyebrow" style={{ color: "rgba(234,221,203,0.7)" }}>
                  Video unavailable
                </div>
                <div className="font-serif text-xl mt-1">We can&apos;t start the video right now</div>
                <p className="text-sm mt-2 max-w-xs mx-auto opacity-80">
                  Please continue by message, or ask the front desk to reach your doctor.
                  We&apos;re working on it — try again in a few minutes.
                </p>
              </>
            )}
            {error && (
              <div className="mt-3 text-xs text-red-200" data-testid="livekit-error">
                {error}
              </div>
            )}
          </div>
        )}
        {canConnect && status === "joining" && (
          <div className="text-center text-cream/80 pointer-events-auto">
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <div className="text-sm mt-3">Connecting to secure room…</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Backwards-compat helper for the old callers: they used
// `jitsiCommand(api, 'toggleAudio' | 'toggleVideo' | 'hangup')`.
// Now `api` is an object literal with those methods, so the helper is a
// tiny dispatcher.
export const livekitCommand = (api, cmd) => {
  try {
    if (!api) return;
    if (cmd === "toggleAudio") return api.toggleAudio();
    if (cmd === "toggleVideo") return api.toggleVideo();
    if (cmd === "hangup") return api.hangup();
  } catch {
    /* ignore */
  }
};
